// Puts a project's pages, or one pane conversation, in an agent slot. Two things follow
// from it, each in one place: useAgentsHttp adds the slot to every call made beneath it,
// and a slot other than default reads through a query cache of its own, so no hook's key
// needs the slot and a switch never shows another slot's cached state.
import { type ReactNode, useMemo } from "react";
import { type QueryClient, QueryClientProvider } from "react-query";

import { AgentSlotContext, DEFAULT_SLOT } from "./agentSlot";
import { makeQueryClient } from "./queryClient";

const slotClients = new Map<string, QueryClient>();

const clientFor = (projectId: string, slot: string): QueryClient => {
  const key = `${projectId}\u0000${slot}`;
  let client = slotClients.get(key);
  if (!client) {
    client = makeQueryClient();
    slotClients.set(key, client);
  }
  return client;
};

export const AgentSlotProvider = ({
  projectId,
  slot,
  children,
}: {
  projectId: string;
  slot: string;
  children: ReactNode;
}) => {
  const scope = useMemo(() => ({ projectId, slot }), [projectId, slot]);
  const scoped = <AgentSlotContext.Provider value={scope}>{children}</AgentSlotContext.Provider>;
  if (slot === DEFAULT_SLOT) return scoped;
  return <QueryClientProvider client={clientFor(projectId, slot)}>{scoped}</QueryClientProvider>;
};
